import * as THREE from 'three';
import type { Aircraft, Aviation, FlightLeg } from './aviation';
import type { Vec } from '../destruction/structure';

export const STRIKE_LENGTH = 192;
export const STRIKE_RADIUS = 19;
const GRAVITY = 9.81;
interface Run {
  aircraft: Aircraft;
  owner: 'player' | 'friendly' | 'hostile';
  center: THREE.Vector3;
  direction: THREE.Vector3;
  resume: FlightLeg[];
  attack: FlightLeg[];
  started: boolean;
  owned: boolean;
  released: number;
  age: number;
  strength: number;
}
interface Bomb {
  p: THREE.Vector3;
  previous: THREE.Vector3;
  v: THREE.Vector3;
  age: number;
  strength: number;
  owner: Run['owner'];
}
/** Finite, rendered flight/bomb jobs. All impacts enter ordinary damage/fire;
 * cancellation stops pending ordnance and reset clears the lifecycle. */
export class Airstrikes {
  runs: Run[] = [];
  bombs: Bomb[] = [];
  strength = 1;
  released = 0;
  impacts = 0;
  completed = 0;
  private epoch: number;
  private serial = 0;
  private mesh = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.48, 6, 4),
    new THREE.MeshStandardMaterial({ color: 0x282d29, roughness: 0.8 }),
    48,
  );
  constructor(readonly aviation: Aviation) {
    this.epoch = aviation.physics.life.epoch;
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    aviation.physics.scene.add(this.mesh);
  }
  request(point: Vec, heading: Vec, owner: Run['owner'] = 'player', existing?: Aircraft): boolean {
    if (
      this.aviation.physics.life.phase !== 'playing' ||
      (owner !== 'player' && this.runs.filter((r) => r.owner !== 'player').length >= 3) ||
      (existing !== undefined && this.runs.some((r) => r.aircraft === existing))
    )
      return false;
    if (existing && (existing.state === 'wreck' || existing.p.y < 65)) return false;
    const direction = new THREE.Vector3(heading.x, 0, heading.z).normalize();
    if (direction.lengthSq() < 0.9) return false;
    const center = new THREE.Vector3(point.x, point.y, point.z);
    const start = center
      .clone()
      .addScaledVector(direction, -1700)
      .setY(
        Math.max(360, point.y + 220) +
          (owner === 'player' ? this.runs.filter((r) => r.owner === 'player').length * 14 : 0),
      );
    const aircraft =
      existing ??
      this.aviation.createMission(
        `player-strike:${++this.serial}`,
        'friendly',
        start,
        direction,
        'regional-jet',
      );
    const resume = aircraft.route.slice();
    const end = center
      .clone()
      .addScaledVector(direction, existing ? 900 : 3300)
      .setY(start.y);
    // Stop the drop queue and let original occupants retrace the cabin before
    // banking. Turning a transport with somebody still beside its hatch hit
    // that real person with the tail, even after the query-frame repair.
    if (existing) this.aviation.troops.cancel(existing);
    this.runs.push({
      aircraft,
      owner,
      center,
      direction,
      resume,
      owned: !existing,
      released: 0,
      age: 0,
      strength: owner === 'player' ? this.strength : 1,
      started: false,
      attack: [
        { point: start, speed: 110, state: 'approach' },
        { point: end, speed: 110, state: 'excursion' },
      ],
    });
    return true;
  }
  busy(a: Aircraft): boolean {
    return this.runs.some((r) => r.aircraft === a);
  }
  private finish(r: Run): void {
    if (r.aircraft.state !== 'wreck') {
      if (r.owned) this.aviation.retireMission(r.aircraft);
      else if (r.started)
        this.aviation.routeMission(r.aircraft, r.resume, 'Bombing pass complete; original flight continuing');
    }
    this.runs.splice(this.runs.indexOf(r), 1);
    this.completed++;
  }
  cancel(owner?: Run['owner']): void {
    for (const r of [...this.runs]) if (!owner || r.owner === owner) this.finish(r);
    this.bombs = this.bombs.filter((b) => owner && b.owner !== owner);
  }
  clear(): void {
    this.cancel();
    this.epoch = this.aviation.physics.life.epoch;
    this.released = this.impacts = this.completed = 0;
    this.mesh.count = 0;
  }
  update(dt: number): void {
    if (this.epoch !== this.aviation.physics.life.epoch) {
      this.clear();
      return;
    }
    const { physics, population } = this.aviation;
    for (const r of [...this.runs]) {
      const a = r.aircraft;
      r.age += dt;
      if (a.state === 'wreck' || r.age > 100) {
        this.finish(r);
        continue;
      }
      if (!r.started) {
        if (!this.aviation.troops.manoeuvreReady(a)) continue;
        r.started = this.aviation.routeMission(
          a,
          r.attack,
          `${r.owner} bombing run · eight physical bombs along the marked strip`,
        );
        if (!r.started) {
          this.finish(r);
          continue;
        }
      }
      if (!a.route.length) {
        this.finish(r);
        continue;
      }
      // Predict the real ballistic landing point; never trigger a remote strip blast.
      const fall =
        (a.velocity.y + Math.sqrt(a.velocity.y ** 2 + 2 * GRAVITY * Math.max(1, a.p.y - r.center.y))) /
        GRAVITY;
      const impact = a.p.clone().addScaledVector(a.velocity, fall);
      const relative = impact.clone().sub(r.center);
      const along = relative.dot(r.direction),
        cross = Math.abs(relative.x * r.direction.z - relative.z * r.direction.x);
      const aligned = a.velocity.clone().setY(0).normalize().dot(r.direction) > 0.985;
      if (
        a.route.length === 1 &&
        r.released < 8 &&
        aligned &&
        cross < 18 &&
        along >= -STRIKE_LENGTH / 2 + r.released * (STRIKE_LENGTH / 7) &&
        along < STRIKE_LENGTH / 2 + 45
      ) {
        const p = a.p.clone().add(new THREE.Vector3(0, -0.8, 0));
        this.bombs.push({
          p,
          previous: p.clone(),
          v: a.velocity.clone(),
          age: 0,
          owner: r.owner,
          strength: r.strength,
        });
        r.released++;
        this.released++;
      }
    }
    for (const b of [...this.bombs]) {
      b.previous.copy(b.p);
      b.age += dt;
      b.v.y -= GRAVITY * dt;
      const move = b.v.clone().multiplyScalar(dt),
        distance = move.length();
      const hit = physics.ray(b.p, move.clone().normalize(), distance, undefined, { physical: true });
      if (hit) {
        if (b.strength > 0)
          physics.blast(
            hit.point,
            STRIKE_RADIUS * Math.cbrt(b.strength),
            (b.owner === 'hostile' ? 30 : 24) * b.strength,
            true,
            'airstrike',
            0.9,
          );
        if (b.strength > 0) {
          population.effects.burst(hit.point, 0x978a79, 36, 8);
          population.audio.play('blast', 1.6, hit.point);
        }
        this.impacts++;
      } else b.p.add(move);
      if (hit || b.age > 30 || b.p.y < -50) this.bombs.splice(this.bombs.indexOf(b), 1);
    }
  }
  render(alpha: number): void {
    const matrix = new THREE.Matrix4(),
      q = new THREE.Quaternion(),
      scale = new THREE.Vector3(0.65, 1.8, 0.65);
    if (this.bombs.length > this.mesh.instanceMatrix.count) {
      const old = this.mesh;
      this.mesh = new THREE.InstancedMesh(
        old.geometry,
        old.material,
        2 ** Math.ceil(Math.log2(this.bombs.length)),
      );
      this.mesh.frustumCulled = false;
      this.aviation.physics.scene.add(this.mesh);
      old.removeFromParent();
      old.dispose();
    }
    this.mesh.count = this.bombs.length;
    for (let i = 0; i < this.mesh.count; i++) {
      const b = this.bombs[i];
      q.setFromUnitVectors(new THREE.Vector3(0, -1, 0), b.v.clone().normalize());
      matrix.compose(b.previous.clone().lerp(b.p, alpha), q, scale);
      this.mesh.setMatrixAt(i, matrix);
    }
    if (this.mesh.count) this.mesh.instanceMatrix.needsUpdate = true;
  }
  snapshot(): Record<string, number> {
    return {
      airstrikeRuns: this.runs.length,
      airstrikeBombs: this.bombs.length,
      airstrikeReleased: this.released,
      airstrikeImpacts: this.impacts,
      airstrikeCompleted: this.completed,
    };
  }
  dispose(): void {
    this.clear();
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
